import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, generateReport, Report, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { ButtonAPI, DeckAPI, FetchAPI, FieldAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService, SessionStorageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { NegotiationProposalAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

enum ProposalStatus {
  TEMPORARY = '1',
  PENDING = '2',
  APPROVED = '3',
  DENIED = '4',
  EXPIRED = '5',
  CANCELED = '6'
}

enum NegotiationType {
  PROPOSAL = 1,
  DRAFT = 2,
  READJUSTMENT = 4,
}

interface ProposalData {
  dsStatus: string,
  ieStatus: ProposalStatus,
  nrSeqProposal: number,
  message: string,
  nrSeqClassification: NegotiationType,
  dsCancelReason: string,
  ieNewProposal: string,
}

const NEGOTIATION_PROPOSAL = '33';

@Controller({ fragment: FragmentNames.NEGOTIATION_PROPOSAL })
class NegotiationProposalController extends BaseController {

  @Inject(Aliases.PANEL_REPORTS)
  private panelReports: PanelAPI;
  @Inject(Aliases.PANEL_REFUSE)
  private panelRefuse: PanelAPI;
  @Inject(Aliases.PANEL_ACCEPT)
  private panelAccept: PanelAPI;
  @Inject(Aliases.PANEL_NEW_PROPOSAL)
  private panelNewProposal: PanelAPI;
  @Inject(Aliases.FETCH_NEGOTIATION_PROPOSAL)
  private fetchProposal: FetchAPI;
  @Inject(Aliases.FIELD_CANCEL_REASON)
  private fieldCancelReason: FieldAPI;
  @Inject(Aliases.BUTTON_ACCEPT)
  private buttonAccept: ButtonAPI;
  @Inject(Aliases.BUTTON_REFUSE)
  private buttonRefuse: ButtonAPI;
  @Inject(Aliases.BUTTON_NEW_PROPOSAL)
  private buttomNewProposal: ButtonAPI;

  @OnMount(Aliases.FETCH_NEGOTIATION_PROPOSAL)
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    api.setParameters({ nrSeqNegotiation, nrSeqProposal });
    api.forceFetch();
  }

  @OnDynamicContentRender(Aliases.FIELD_STATUS)
  onMountStatus(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as ProposalData;
    if (ieStatus) {
      const content = (): ReactElement => (getStampStatusProposal(ieStatus, dsStatus));
      render(content);
    }
  }

  @OnMount(Aliases.FETCH_REPORTS)
  onMountFetchReport(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const ieProposalType = SessionStorageService.getItem('nrSeqClassification');
    api.setParameters({ cdGroup: NEGOTIATION_PROPOSAL, ieProposalType }); api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_REPORTS)
  onAfterFetchReports(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelReports.setVisible(false);
    }
  }

  @OnAfterFetch(Aliases.FETCH_NEGOTIATION_PROPOSAL)
  onAfterFetchProposal(event: FetchEvent): void {
    if (event.data) {
      const { ieStatus, dsCancelReason, ieNewProposal } = event.data as ProposalData;
      this.fieldCancelReason.setVisible(!isEmptyArrayOrObject(dsCancelReason));
      this.buttonAccept.setVisible(ieStatus == ProposalStatus.PENDING);
      this.buttonRefuse.setVisible(ieStatus == ProposalStatus.PENDING);
      this.buttomNewProposal.setVisible(ieNewProposal == 'S');
    }
  }

  @OnClick(Aliases.BUTTON_REFUSE)
  onClickRefuse(): void {
    this.panelRefuse.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_REFUSE)
  onRefuseProposal(event: SubmitEvent): void {
    const { message } = event.record as ProposalData;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.NEGOTIATION_PROPOSAL_REJECT, { nrSeqProposal, nrSeqNegotiation, message }).then(() => {
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.request-success'));
      this.fetchProposal.forceFetch();
      this.panelRefuse.setVisible(false);
    }).catch(() => ToastServiceProviderAccreditation.error('pa.error-process-request'));
  }

  @OnClose(Aliases.MODAL_REFUSE)
  onCloseModalRefuse(): void {
    this.panelRefuse.setVisible(false);
  }
  @OnCancel(Aliases.FORM_REFUSE)
  onCancelFormRefuse(): void {
    this.panelRefuse.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_ACCEPT)
  onClickAccept(): void {
    this.panelAccept.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_ACCEPT)
  onAcceptProposal(event: SubmitEvent): void {
    const { message } = event.record as ProposalData;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.NEGOTIATION_PROPOSAL_ACCEPT, { nrSeqProposal, nrSeqNegotiation, message }).then(() => {
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.request-success'));
      this.fetchProposal.forceFetch();
      this.panelAccept.setVisible(false);
    }).catch((error) => {
      this.panelAccept.setVisible(false);
      ToastServiceProviderAccreditation.error(error.body);
    });
  }

  @OnClose(Aliases.MODAL_ACCEPT)
  onCloseModalAccept(): void {
    this.panelAccept.setVisible(false);
  }

  @OnCancel(Aliases.FORM_ACCEPT)
  onCancelFormAccept(): void {
    this.panelAccept.setVisible(false);
  }

  @OnSelectItem(Aliases.DECK_REPORTS)
  onselect(event: SelectItemEvent<Report>): void {
    const { nrSeqNegotiation, nrSeqProposal } = this.historyService.getCurrentRouteParams();
    const api = event.target as unknown as DeckAPI;
    const requestParams = {
      nrSeqNegotiation,
      nrSeqProposal,
    };

    api.unselectAll();

    generateReport(Endpoint.POST.NEGOTIATION_REPORT, requestParams, NEGOTIATION_PROPOSAL, event.item)
      .catch(() => ToastServiceProviderAccreditation.error('pa.error-process-request'))
      .finally(() => this.changeCursor(false));
  }

  @OnClose(Aliases.MODAL_NEW_PROPOSAL)
  onCloseModalNewProposal(): void {
    this.panelNewProposal.setVisible(false);
  }
  @OnCancel(Aliases.FORM_NEW_PROPOSAL)
  onCancelFormNewProposal(): void {
    this.panelNewProposal.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_NEW_PROPOSAL)
  onClickNewProposal(): void {
    this.panelNewProposal.setVisible(true);
  }

  @OnSubmit(Aliases.FORM_NEW_PROPOSAL)
  onNewProposal(event: SubmitEvent): void {
    const { message } = event.record as ProposalData;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    HttpService.post(Endpoint.POST.NEGOTIATION_NEW_PROPOSAL, { nrSeqProposal, nrSeqNegotiation, message }).then(() => {
      ToastServiceProviderAccreditation.success(MessageService.translate('pa.request-success'));
      this.fetchProposal.forceFetch();
      this.panelNewProposal.setVisible(false);
    }).catch((error) => {
      this.panelNewProposal.setVisible(false);
      ToastServiceProviderAccreditation.error(error.body);
    });
  }

  private changeCursor(isProgress: boolean): void {
    document.body.style.cursor = isProgress ? 'wait' : 'default';
  }

}

function getStampStatusProposal(ieStatus: ProposalStatus, label: string): ReactElement {
  let stampProps = null;

  const icon = {
    [ProposalStatus.TEMPORARY]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label },
    [ProposalStatus.PENDING]: { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label },
    [ProposalStatus.APPROVED]: { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label },
    [ProposalStatus.DENIED]: { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label },
    [ProposalStatus.EXPIRED]: { className: 'c-stamp-custom-color-purple', icon: StampIcon.CANCELED_OUTLINE, label },
    [ProposalStatus.CANCELED]: { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CANCELED_OUTLINE, label },
  };

  stampProps = icon[ieStatus];
  return <Stamp {...stampProps} />;
}

export {
  NegotiationProposalController,
  getStampStatusProposal,
  ProposalData,
  ProposalStatus,
  NegotiationType,
};
